/**
 * DataBest — Auth store (zustand + localStorage).
 * Holds user + tokens. Exposes login/logout/refresh actions.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import authApi from "@/features/auth/api/authApi";
import useAlertsStore from "@/store/alertsStore";

const STORAGE_KEY = "databest.auth";

const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            accessToken: null,
            refreshToken: null,
            initializing: true,

            login: async (email, password) => {
                const data = await authApi.login(email, password);
                set({
                    user: data.user,
                    accessToken: data.access_token,
                    refreshToken: data.refresh_token,
                });
                return data.user;
            },

            logout: async () => {
                try {
                    await authApi.logout();
                } catch (_) {
                    // ignore — clear session locally anyway
                }
                useAlertsStore.getState().reset();
                set({ user: null, accessToken: null, refreshToken: null });
            },

            hydrate: async () => {
                // On boot, if we have a token, fetch /me to validate the session.
                if (!get().accessToken) {
                    set({ initializing: false });
                    return;
                }
                try {
                    const me = await authApi.me();
                    set({ user: me, initializing: false });
                } catch (_) {
                    set({
                        user: null,
                        accessToken: null,
                        refreshToken: null,
                        initializing: false,
                    });
                }
            },

            isAuthenticated: () => !!get().accessToken && !!get().user,
        }),
        {
            name: STORAGE_KEY,
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                user: state.user,
                accessToken: state.accessToken,
                refreshToken: state.refreshToken,
            }),
        },
    ),
);

export default useAuthStore;
